import React, { useState } from 'react';
import { LineChart, Line, PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { Plus } from 'lucide-react';
import { db } from '../firebase';
import { collection, addDoc, onSnapshot, doc, updateDoc, deleteDoc, serverTimestamp, query, orderBy } from 'firebase/firestore';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../contexts/ThemeContext';
import BackButton from '../components/BackButton';
import PortalBackground from '../components/PortalBackground';

const TechnologyReports = () => {
  const navigate = useNavigate();
  const { darkMode } = useTheme();
  
  
  const reportData = {
    systemData: [
      { month: 'Jan', activeUsers: 85, avgSessionTime: 45, totalUsers: 120 },
      { month: 'Feb', activeUsers: 88, avgSessionTime: 48, totalUsers: 120 },
      { month: 'Mar', activeUsers: 92, avgSessionTime: 50, totalUsers: 120 },
      { month: 'Apr', activeUsers: 90, avgSessionTime: 52, totalUsers: 120 },
      { month: 'May', activeUsers: 95, avgSessionTime: 55, totalUsers: 120 },
      { month: 'Jun', activeUsers: 102, avgSessionTime: 58, totalUsers: 120 }
    ],
    apiCostsData: [
      { month: 'Jan', openai: 1250, stripe: 450, twilio: 320, google: 280 },
      { month: 'Feb', openai: 1380, stripe: 480, twilio: 340, google: 290 },
      { month: 'Mar', openai: 1520, stripe: 520, twilio: 360, google: 310 },
      { month: 'Apr', openai: 1680, stripe: 580, twilio: 380, google: 330 },
      { month: 'May', openai: 1850, stripe: 620, twilio: 400, google: 350 },
      { month: 'Jun', openai: 2100, stripe: 680, twilio: 420, google: 380 }
    ],
    automationData: [
      { month: 'Jan', coverage: 65, avgTurnaround: 14 },
      { month: 'Feb', coverage: 68, avgTurnaround: 12 },
      { month: 'Mar', coverage: 72, avgTurnaround: 10 },
      { month: 'Apr', coverage: 75, avgTurnaround: 9 },
      { month: 'May', coverage: 78, avgTurnaround: 8 },
      { month: 'Jun', coverage: 82, avgTurnaround: 7 }
    ],
    dataSegmentation: [
      { name: 'Client Data', value: 45, color: '#5AB9EA' },
      { name: 'Internal Data', value: 25, color: '#84CEEB' },
      { name: 'Analytics', value: 20, color: '#5680E9' },
      { name: 'Backup', value: 10, color: '#F8A055' }
    ],
    bugReportData: [
      { month: 'Jan', crmBugs: 15, portalBugs: 8 },
      { month: 'Feb', crmBugs: 12, portalBugs: 6 },
      { month: 'Mar', crmBugs: 10, portalBugs: 5 },
      { month: 'Apr', crmBugs: 8, portalBugs: 4 },
      { month: 'May', crmBugs: 6, portalBugs: 3 },
      { month: 'Jun', crmBugs: 5, portalBugs: 2 }
    ],
    performanceData: [
      { month: 'Jan', activeUsers: 1200, avgSessionTime: 15, pageViews: 8500 },
      { month: 'Feb', activeUsers: 1350, avgSessionTime: 18, pageViews: 9200 },
      { month: 'Mar', activeUsers: 1420, avgSessionTime: 20, pageViews: 9800 },
      { month: 'Apr', activeUsers: 1580, avgSessionTime: 22, pageViews: 10500 },
      { month: 'May', activeUsers: 1650, avgSessionTime: 25, pageViews: 11200 },
      { month: 'Jun', activeUsers: 1800, avgSessionTime: 28, pageViews: 12500 }
    ],
    projectData: [
      { project: 'Frontend', progress: 85, health: 'Good' },
      { project: 'Backend', progress: 92, health: 'Excellent' },
      { project: 'Mobile', progress: 78, health: 'Good' },
      { project: 'DevOps', progress: 88, health: 'Good' },
      { project: 'Security', progress: 95, health: 'Excellent' },
      { project: 'AI/ML', progress: 65, health: 'Fair' }
    ],
    kpiData: [
      { metric: 'System Uptime', target: 99.9, actual: 99.9, variance: 0 },
      { metric: 'Response Time', target: 150, actual: 120, variance: -20 },
      { metric: 'Bug Resolution', target: 95, actual: 94, variance: -1 },
    ],
    metrics: {
      overallUptime: 99.9,
      totalIncidents: 28,
      avgResponseTime: '120ms',
      securityScore: 92,
      codeQuality: 88,
      deploymentSuccess: 98.5,
      testCoverage: 85,
      technicalDebt: 12,
      clientLoginFrequency: 12.5,
      automationCoverage: 78,
      totalBugReports: 156,
      internalBugs: 89,
      clientBugs: 67,
      dataCollected: 12500
    },
    topProjects: [
      { title: 'Cloud Migration', impact: '40% cost reduction' },
      { title: 'CI/CD Pipeline', impact: '60% faster deployments' },
      { title: 'Security Audit', impact: '25% risk reduction' }
    ]
  };

  const [subscriptions, setSubscriptions] = useState([]);
  const [isAddOpen, setIsAddOpen] = useState(false);
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const [newSubscription, setNewSubscription] = useState({
    program: '',
    amount: '',
    period: 'Monthly',
    startDate: '',
    endDate: '',
    status: 'active',
    description: ''
  });
  const [editSubscription, setEditSubscription] = useState({
    program: '',
    amount: '',
    period: 'Monthly',
    startDate: '',
    endDate: '',
    status: 'active',
    description: ''
  });

  const handleAddSubscription = async (e) => {
    e.preventDefault();
    const amountNumber = Number(newSubscription.amount);
    if (!newSubscription.program || isNaN(amountNumber) || !newSubscription.startDate) return;
    await addDoc(collection(db, 'technologySubscriptions'), {
      program: newSubscription.program,
      amount: amountNumber,
      period: newSubscription.period,
      startDate: newSubscription.startDate,
      endDate: newSubscription.endDate || null,
      status: newSubscription.status,
      description: newSubscription.description,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    });
    setNewSubscription({ program: '', amount: '', period: 'Monthly', startDate: '', endDate: '', status: 'active', description: '' });
    setIsAddOpen(false);
  };

  const openAdd = (asUpcoming = false) => {
    setNewSubscription({
      program: '',
      amount: '',
      period: 'Monthly',
      startDate: '',
      endDate: '',
      status: asUpcoming ? 'upcoming' : 'active',
      description: ''
    });
    setIsAddOpen(true);
  };

  // Realtime shared subscriptions
  React.useEffect(() => {
    const col = collection(db, 'technologySubscriptions');
    const qy = query(col, orderBy('createdAt', 'desc'));
    const unsub = onSnapshot(qy, (snap) => {
      const list = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      setSubscriptions(list);
    });
    return () => unsub();
  }, []);

  const openEdit = (sub, idx) => {
    setSelectedId(sub.id || null);
    setEditSubscription({
      program: sub.program,
      amount: String(sub.amount),
      period: sub.period,
      startDate: sub.startDate,
      endDate: sub.endDate || '',
      status: sub.status,
      description: sub.description || ''
    });
    setIsEditOpen(true);
  };

  const handleUpdateSubscription = async (e) => {
    e.preventDefault();
    if (!selectedId) return;
    const amountNumber = Number(editSubscription.amount);
    if (!editSubscription.program || isNaN(amountNumber) || !editSubscription.startDate) return;
    await updateDoc(doc(db, 'technologySubscriptions', selectedId), {
      program: editSubscription.program,
      amount: amountNumber,
      period: editSubscription.period,
      startDate: editSubscription.startDate,
      endDate: editSubscription.endDate || null,
      status: editSubscription.status,
      description: editSubscription.description,
      updatedAt: serverTimestamp()
    });
    setIsEditOpen(false);
    setSelectedId(null);
  };

  const handleDeleteSubscription = async () => {
    if (!selectedId) return;
    await deleteDoc(doc(db, 'technologySubscriptions', selectedId));
    setIsEditOpen(false);
    setSelectedId(null);
  };

  // Calculate metrics
  const totalIncidents = reportData.performanceData.reduce((sum, item) => sum + item.incidents, 0);
  const avgUptime = reportData.performanceData.reduce((sum, item) => sum + item.uptime, 0) / reportData.performanceData.length;
  const avgResponseTime = reportData.performanceData.reduce((sum, item) => sum + item.responseTime, 0) / reportData.performanceData.length;
  const projectHealth = reportData.projectData.reduce((sum, item) => sum + (item.health === 'Excellent' ? 100 : item.health === 'Good' ? 80 : 60), 0) / reportData.projectData.length;

  

  const costPerMonth = reportData.apiCostsData.map(item => ({
    month: item.month,
    cost: (item.openai || 0) + (item.stripe || 0) + (item.twilio || 0) + (item.google || 0)
  }));

  const getMonthlyEquivalent = (sub) => {
    if (!sub || typeof sub.amount !== 'number') return 0;
    if (sub.period === 'Monthly') return sub.amount;
    if (sub.period === 'Quarterly') return sub.amount / 3;
    if (sub.period === 'Yearly') return sub.amount / 12;
    return sub.amount; // default fallback
  };

  const monthlySoftwareCost = subscriptions
    .filter(sub => sub.status !== 'inactive')
    .reduce((sum, sub) => sum + getMonthlyEquivalent(sub), 0);
  const annualSoftwareCost = monthlySoftwareCost * 12;

  const upcomingSubscriptions = subscriptions.filter(s => s.status === 'upcoming');
  const futureMonthlyAdd = upcomingSubscriptions.reduce((sum, sub) => sum + getMonthlyEquivalent(sub), 0);
  const futureMonthlyTotal = monthlySoftwareCost + futureMonthlyAdd;
  const futureAnnualTotal = futureMonthlyTotal * 12;

  const getQuarterLabel = (dateStr) => {
    if (!dateStr) return '—';
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return '—';
    const q = Math.floor(d.getMonth() / 3) + 1;
    return `Q${q} ${d.getFullYear()}`;
  };

  return (
    <div className="min-h-screen p-6 relative">
      <PortalBackground />
      <div className="relative max-w-7xl mx-auto">
        <div className="flex items-center justify-between mb-8">
          <BackButton onClick={() => navigate('/crm/reports')} />
          <h1 className={`text-3xl font-bold tracking-tight flex-1 text-center text-gray-900 dark:text-white`}>Technology Analytics</h1>
          <div className="w-[100px]"></div> {/* Spacer to balance the back button */}
        </div>

        

        

        {/* Subscriptions Table */}
        <div className={`bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800 shadow-sm p-6 mb-6`}>
          <div className="relative mb-4">
            <h2 className={`text-xl font-semibold text-center ${darkMode ? 'text-white' : 'text-gray-900'}`}>Active Subscriptions</h2>
            <button
              onClick={() => openAdd(false)}
              aria-label="Add New Subscription"
              className={`absolute right-0 top-1/2 -translate-y-1/2 inline-flex items-center justify-center h-10 w-10 rounded-full ${darkMode ? 'bg-red-600 hover:bg-red-700 text-white' : 'bg-red-600 hover:bg-red-700 text-white'}`}
            >
              <Plus className="h-5 w-5" />
            </button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-4">
            <div className={`flex flex-col items-center p-4 rounded-xl bg-green-50 dark:bg-green-900/40 border border-green-100 dark:border-green-800/50`}>
              <span className={`text-sm text-center ${darkMode ? 'text-green-200' : 'text-gray-600'}`}>Monthly (Active)</span>
              <span className={`text-2xl font-bold text-center ${darkMode ? 'text-green-100' : 'text-gray-900'}`}>{`$${monthlySoftwareCost.toLocaleString(undefined, { maximumFractionDigits: 2 })}`}</span>
            </div>
            <div className={`flex flex-col items-center p-4 rounded-xl bg-green-50 dark:bg-green-900/40 border border-green-100 dark:border-green-800/50`}>
              <span className={`text-sm text-center ${darkMode ? 'text-green-200' : 'text-gray-600'}`}>Annual (Active)</span>
              <span className={`text-2xl font-bold text-center ${darkMode ? 'text-green-100' : 'text-gray-900'}`}>{`$${annualSoftwareCost.toLocaleString(undefined, { maximumFractionDigits: 2 })}`}</span>
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className={`min-w-full divide-y ${darkMode ? 'divide-gray-700' : 'divide-gray-200'}`}>
              <thead className={darkMode ? 'bg-gray-700' : 'bg-gray-50'}>
                <tr>
                  <th className={`px-4 py-3 text-left text-xs font-medium uppercase tracking-wider ${darkMode ? 'text-gray-200' : 'text-gray-700'}`}>Program</th>
                  <th className={`px-4 py-3 text-left text-xs font-medium uppercase tracking-wider ${darkMode ? 'text-gray-200' : 'text-gray-700'}`}>Amount</th>
                  <th className={`px-4 py-3 text-left text-xs font-medium uppercase tracking-wider ${darkMode ? 'text-gray-200' : 'text-gray-700'}`}>Period</th>
                  <th className={`px-4 py-3 text-left text-xs font-medium uppercase tracking-wider ${darkMode ? 'text-gray-200' : 'text-gray-700'}`}>Start Date</th>
                  <th className={`px-4 py-3 text-left text-xs font-medium uppercase tracking-wider ${darkMode ? 'text-gray-200' : 'text-gray-700'}`}>End Date</th>
                  <th className={`px-4 py-3 text-left text-xs font-medium uppercase tracking-wider ${darkMode ? 'text-gray-200' : 'text-gray-700'}`}>Status</th>
                </tr>
              </thead>
              <tbody className={darkMode ? 'bg-gray-800 divide-y divide-gray-700' : 'bg-white divide-y divide-gray-200'}>
                {subscriptions.filter(s => s.status !== 'upcoming').map((sub, idx) => {
                  const rowColor = sub.status === 'phasing out'
                    ? (darkMode ? 'bg-yellow-900/30' : 'bg-yellow-50')
                    : sub.status === 'need to kill'
                      ? (darkMode ? 'bg-red-900/30' : 'bg-red-50')
                      : sub.status === 'inactive'
                        ? (darkMode ? 'bg-gray-800/50' : 'bg-gray-50')
                        : '';
                  const dimText = sub.status === 'inactive' ? (darkMode ? 'text-gray-400' : 'text-gray-500') : '';
                  return (
                  <tr
                    key={idx}
                    onClick={() => openEdit(sub, idx)}
                    className={`${rowColor} ${darkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-50'} cursor-pointer`}
                  >
                    <td className={`px-4 py-3 whitespace-nowrap ${dimText || (darkMode ? 'text-gray-100' : 'text-gray-900')}`}>{sub.program}</td>
                    <td className={`px-4 py-3 whitespace-nowrap ${dimText || (darkMode ? 'text-gray-100' : 'text-gray-900')}`}>{`$${sub.amount.toLocaleString()}`}</td>
                    <td className={`px-4 py-3 whitespace-nowrap ${dimText || (darkMode ? 'text-gray-100' : 'text-gray-900')}`}>{sub.period}</td>
                    <td className={`px-4 py-3 whitespace-nowrap ${dimText || (darkMode ? 'text-gray-100' : 'text-gray-900')}`}>{sub.startDate}</td>
                    <td className={`px-4 py-3 whitespace-nowrap ${dimText || (darkMode ? 'text-gray-100' : 'text-gray-900')}`}>{sub.endDate || '—'}</td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                        sub.status === 'active'
                          ? (darkMode ? 'bg-green-900 text-green-200' : 'bg-green-100 text-green-800')
                          : sub.status === 'upcoming'
                            ? (darkMode ? 'bg-blue-900 text-blue-200' : 'bg-blue-100 text-blue-800')
                            : sub.status === 'inactive'
                              ? (darkMode ? 'bg-gray-800 text-gray-400' : 'bg-gray-100 text-gray-500')
                              : sub.status === 'phasing out'
                                ? (darkMode ? 'bg-yellow-900 text-yellow-200' : 'bg-yellow-100 text-yellow-800')
                                : (darkMode ? 'bg-red-900 text-red-200' : 'bg-red-100 text-red-800')
                      }`}>{sub.status}</span>
                    </td>
                  </tr>
                  );
                })}
              </tbody>
            </table>
            </div>
          </div>
        {/* Future Subscriptions Overview */}
        <div className={`bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800 shadow-sm p-6 mb-6`}>
          <div className="relative mb-4">
            <h2 className={`text-xl font-semibold text-center ${darkMode ? 'text-white' : 'text-gray-900'}`}>Future Subscriptions</h2>
            <button
              onClick={() => openAdd(true)}
              aria-label="Add New Future Subscription"
              className={`absolute right-0 top-1/2 -translate-y-1/2 inline-flex items-center justify-center h-10 w-10 rounded-full ${darkMode ? 'bg-red-600 hover:bg-red-700 text-white' : 'bg-red-600 hover:bg-red-700 text-white'}`}
            >
              <Plus className="h-5 w-5" />
            </button>
          </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
            <div className={`flex flex-col items-center p-4 rounded-xl bg-blue-50 dark:bg-blue-900/40 border border-blue-100 dark:border-blue-800/50`}>
              <span className={`text-sm text-center ${darkMode ? 'text-blue-200' : 'text-gray-600'}`}>Future Monthly (with additions)</span>
              <span className={`text-2xl font-bold text-center ${darkMode ? 'text-blue-100' : 'text-gray-900'}`}>{`$${futureMonthlyTotal.toLocaleString(undefined, { maximumFractionDigits: 2 })}`}</span>
            </div>
            <div className={`flex flex-col items-center p-4 rounded-xl bg-blue-50 dark:bg-blue-900/40 border border-blue-100 dark:border-blue-800/50`}>
              <span className={`text-sm text-center ${darkMode ? 'text-blue-200' : 'text-gray-600'}`}>Future Annual (with additions)</span>
              <span className={`text-2xl font-bold text-center ${darkMode ? 'text-blue-100' : 'text-gray-900'}`}>{`$${futureAnnualTotal.toLocaleString(undefined, { maximumFractionDigits: 2 })}`}</span>
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className={`min-w-full divide-y ${darkMode ? 'divide-gray-700' : 'divide-gray-200'}`}>
              <thead className={darkMode ? 'bg-gray-700' : 'bg-gray-50'}>
                <tr>
                  <th className={`px-4 py-3 text-left text-xs font-medium uppercase tracking-wider ${darkMode ? 'text-gray-200' : 'text-gray-700'}`}>Program</th>
                  <th className={`px-4 py-3 text-left text-xs font-medium uppercase tracking-wider ${darkMode ? 'text-gray-200' : 'text-gray-700'}`}>Amount</th>
                  <th className={`px-4 py-3 text-left text-xs font-medium uppercase tracking-wider ${darkMode ? 'text-gray-200' : 'text-gray-700'}`}>Period</th>
                  <th className={`px-4 py-3 text-left text-xs font-medium uppercase tracking-wider ${darkMode ? 'text-gray-200' : 'text-gray-700'}`}>Start Quarter</th>
                  <th className={`px-4 py-3 text-left text-xs font-medium uppercase tracking-wider ${darkMode ? 'text-gray-200' : 'text-gray-700'}`}>Description</th>
                </tr>
              </thead>
              <tbody className={darkMode ? 'bg-gray-800 divide-y divide-gray-700' : 'bg-white divide-y divide-gray-200'}>
                {upcomingSubscriptions.map((sub, idx) => (
                  <tr
                    key={idx}
                    onClick={() => openEdit(sub, idx)}
                    className={`${darkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-50'} cursor-pointer`}
                  >
                    <td className={`px-4 py-3 whitespace-nowrap ${darkMode ? 'text-gray-100' : 'text-gray-900'}`}>{sub.program}</td>
                    <td className={`px-4 py-3 whitespace-nowrap ${darkMode ? 'text-gray-100' : 'text-gray-900'}`}>{`$${sub.amount.toLocaleString()}`}</td>
                    <td className={`px-4 py-3 whitespace-nowrap ${darkMode ? 'text-gray-100' : 'text-gray-900'}`}>{sub.period}</td>
                    <td className={`px-4 py-3 whitespace-nowrap ${darkMode ? 'text-gray-100' : 'text-gray-900'}`}>{getQuarterLabel(sub.startDate)}</td>
                    <td className={`px-4 py-3 ${darkMode ? 'text-gray-100' : 'text-gray-900'}`}>{sub.description || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {isAddOpen && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
            <div className={`bg-white dark:bg-gray-900 rounded-2xl shadow-xl p-6 w-full max-w-lg`}>
              <h3 className={`text-lg font-semibold mb-4 text-center ${darkMode ? 'text-white' : 'text-gray-900'}`}>Add Subscription</h3>
              <form onSubmit={handleAddSubscription} className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="md:col-span-2">
                  <label className={`block text-sm mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Program</label>
                  <input
                    type="text"
                    className={`w-full px-3 py-2 rounded-md border ${darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'}`}
                    value={newSubscription.program}
                    onChange={(e) => setNewSubscription(s => ({ ...s, program: e.target.value }))}
                    required
                  />
                </div>
                <div className="md:col-span-2">
                  <label className={`block text-sm mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Description</label>
                  <textarea
                    className={`w-full px-3 py-2 rounded-md border ${darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'}`}
                    rows={3}
                    value={newSubscription.description}
                    onChange={(e) => setNewSubscription(s => ({ ...s, description: e.target.value }))}
                  />
                </div>
                <div>
                  <label className={`block text-sm mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Amount</label>
                  <input
                    type="number"
                    className={`w-full px-3 py-2 rounded-md border ${darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'}`}
                    value={newSubscription.amount}
                    onChange={(e) => setNewSubscription(s => ({ ...s, amount: e.target.value }))}
                    min="0"
                    step="0.01"
                    required
                  />
                </div>
                <div>
                  <label className={`block text-sm mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Period</label>
                  <select
                    className={`w-full px-3 py-2 rounded-md border ${darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'}`}
                    value={newSubscription.period}
                    onChange={(e) => setNewSubscription(s => ({ ...s, period: e.target.value }))}
                  >
                    <option>Monthly</option>
                    <option>Quarterly</option>
                    <option>Yearly</option>
                  </select>
                </div>
                <div>
                  <label className={`block text-sm mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Start Date</label>
                  <input
                    type="date"
                    className={`w-full px-3 py-2 rounded-md border ${darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'}`}
                    value={newSubscription.startDate}
                    onChange={(e) => setNewSubscription(s => ({ ...s, startDate: e.target.value }))}
                    required
                  />
                </div>
                <div>
                  <label className={`block text-sm mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>End Date</label>
                  <input
                    type="date"
                    className={`w-full px-3 py-2 rounded-md border ${darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'}`}
                    value={newSubscription.endDate}
                    onChange={(e) => setNewSubscription(s => ({ ...s, endDate: e.target.value }))}
                  />
                </div>
                <div>
                  <label className={`block text-sm mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Status</label>
                  <select
                    className={`w-full px-3 py-2 rounded-md border ${darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'}`}
                    value={newSubscription.status}
                    onChange={(e) => setNewSubscription(s => ({ ...s, status: e.target.value }))}
                  >
                    <option value="active">active</option>
                    <option value="upcoming">upcoming</option>
                    <option value="inactive">inactive</option>
                    <option value="phasing out">phasing out</option>
                    <option value="need to kill">need to kill</option>
                  </select>
                </div>
                <div className="md:col-span-2 flex justify-end space-x-3 mt-2">
                  <button
                    type="button"
                    onClick={() => setIsAddOpen(false)}
                    className={`${darkMode ? 'bg-gray-700 hover:bg-gray-600 text-white' : 'bg-gray-200 hover:bg-gray-300 text-gray-800'} px-4 py-2 rounded-md`}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className={`${darkMode ? 'bg-red-600 hover:bg-red-700 text-white' : 'bg-red-600 hover:bg-red-700 text-white'} px-4 py-2 rounded-md`}
                  >
                    Add
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {isEditOpen && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
            <div className={`bg-white dark:bg-gray-900 rounded-2xl shadow-xl p-6 w-full max-w-lg`}>
              <h3 className={`text-lg font-semibold mb-4 text-center ${darkMode ? 'text-white' : 'text-gray-900'}`}>Subscription Details</h3>
              <form onSubmit={handleUpdateSubscription} className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="md:col-span-2">
                  <label className={`block text-sm mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Program</label>
                  <input
                    type="text"
                    className={`w-full px-3 py-2 rounded-md border ${darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'}`}
                    value={editSubscription.program}
                    onChange={(e) => setEditSubscription(s => ({ ...s, program: e.target.value }))}
                    required
                  />
                </div>
                <div className="md:col-span-2">
                  <label className={`block text-sm mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Description</label>
                  <textarea
                    className={`w-full px-3 py-2 rounded-md border ${darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'}`}
                    rows={3}
                    value={editSubscription.description}
                    onChange={(e) => setEditSubscription(s => ({ ...s, description: e.target.value }))}
                  />
                </div>
                <div>
                  <label className={`block text-sm mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Amount</label>
                  <input
                    type="number"
                    className={`w-full px-3 py-2 rounded-md border ${darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'}`}
                    value={editSubscription.amount}
                    onChange={(e) => setEditSubscription(s => ({ ...s, amount: e.target.value }))}
                    min="0"
                    step="0.01"
                    required
                  />
                </div>
                <div>
                  <label className={`block text-sm mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Period</label>
                  <select
                    className={`w-full px-3 py-2 rounded-md border ${darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'}`}
                    value={editSubscription.period}
                    onChange={(e) => setEditSubscription(s => ({ ...s, period: e.target.value }))}
                  >
                    <option>Monthly</option>
                    <option>Quarterly</option>
                    <option>Yearly</option>
                  </select>
                </div>
                <div>
                  <label className={`block text-sm mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Start Date</label>
                  <input
                    type="date"
                    className={`w-full px-3 py-2 rounded-md border ${darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'}`}
                    value={editSubscription.startDate}
                    onChange={(e) => setEditSubscription(s => ({ ...s, startDate: e.target.value }))}
                    required
                  />
                </div>
                <div>
                  <label className={`block text-sm mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>End Date</label>
                  <input
                    type="date"
                    className={`w-full px-3 py-2 rounded-md border ${darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'}`}
                    value={editSubscription.endDate}
                    onChange={(e) => setEditSubscription(s => ({ ...s, endDate: e.target.value }))}
                  />
                </div>
                <div>
                  <label className={`block text-sm mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Status</label>
                  <select
                    className={`w-full px-3 py-2 rounded-md border ${darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'}`}
                    value={editSubscription.status}
                    onChange={(e) => setEditSubscription(s => ({ ...s, status: e.target.value }))}
                  >
                    <option value="active">active</option>
                    <option value="upcoming">upcoming</option>
                    <option value="inactive">inactive</option>
                    <option value="phasing out">phasing out</option>
                    <option value="need to kill">need to kill</option>
                  </select>
              </div>
                <div className="md:col-span-2 flex justify-between space-x-3 mt-2">
                  <button
                    type="button"
                    onClick={handleDeleteSubscription}
                    className={`${darkMode ? 'bg-red-900 hover:bg-red-800 text-red-200' : 'bg-red-100 hover:bg-red-200 text-red-800'} px-4 py-2 rounded-md`}
                  >
                    Delete
                  </button>
                  <div className="ml-auto flex space-x-3">
                    <button
                      type="button"
                      onClick={() => setIsEditOpen(false)}
                      className={`${darkMode ? 'bg-gray-700 hover:bg-gray-600 text-white' : 'bg-gray-200 hover:bg-gray-300 text-gray-800'} px-4 py-2 rounded-md`}
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className={`${darkMode ? 'bg-red-600 hover:bg-red-700 text-white' : 'bg-red-600 hover:bg-red-700 text-white'} px-4 py-2 rounded-md`}
                    >
                      Save
                    </button>
              </div>
            </div>
              </form>
            </div>
          </div>
        )}
        

      </div>
    </div>
  );
};

export default TechnologyReports; 